import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ConceptCard, ConceptCardGrid, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/')({
  component: FeaturesPage,
});

const features = [
  {
    title: "2000+ Integrations",
    description:
      "Connect to any service instantly. Pre-built connectors for CRM, ERP, databases, APIs, and more.",
    href: "/features/integrations",
    iconName: "Puzzle",
  },
  {
    title: "Enterprise Security",
    description:
      "Zero-trust architecture with fine-grained policies, field-level access control, and comprehensive audit logging.",
    href: "/features/security",
    iconName: "Shield",
  },
  {
    title: "AI/LLM Gateway",
    description:
      "Unified access to all major AI providers. Manage prompts, track costs, and optimize LLM usage.",
    href: "/features/ai-gateway",
    iconName: "BrainCircuit",
  },
  {
    title: "Visual Workflow Builder",
    description:
      "Build complex automations with drag-and-drop. No code required for common patterns.",
    href: "/features/workflow-engine",
    iconName: "Workflow",
  },
  {
    title: "AI Agents Platform",
    description:
      "Create, deploy, and manage autonomous AI agents that can use tools and make decisions.",
    href: "/features/agents-platform",
    iconName: "Bot",
  },
  {
    title: "Real-time Analytics",
    description:
      "Gain insights into API usage, performance metrics, and business intelligence with customizable dashboards.",
    href: "/features/analytics",
    iconName: "BarChart3",
  },
  {
    title: "SSO & SAML",
    description:
      "Enterprise authentication with SAML, OIDC, and SCIM provisioning for seamless team management.",
    href: "/features/sso-saml",
    iconName: "Key",
  },
];

const highlights = [
  { value: "2000+", label: "Pre-built Integrations" },
  { value: "<10ms", label: "Gateway Latency" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "SOC2", label: "Compliant" },
];

function FeaturesPage() {
  return (
    <div>
      <ContentHeader
        title="Platform Features"
        description="Everything you need to integrate APIs, build AI agents, and automate workflows—all in one enterprise-ready platform."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Features" }]}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
        {highlights.map((stat) => (
          <div key={stat.label} className="text-center p-6 rounded-xl border bg-card">
            <div className="text-3xl font-bold text-primary">{stat.value}</div>
            <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Features Grid */}
      <ConceptCardGrid columns={2}>
        {features.map((feature) => (
          <ConceptCard
            key={feature.href}
            title={feature.title}
            description={feature.description}
            href={feature.href}
            iconName={feature.iconName}
          />
        ))}
      </ConceptCardGrid>

      {/* Additional Features */}
      <div className="mt-16 rounded-2xl bg-muted/50 p-8">
        <h2 className="text-2xl font-bold text-center mb-8">And Much More</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            "Rate Limiting",
            "Caching",
            "Load Balancing",
            "Protocol Translation",
            "Webhook Management",
            "API Key Management",
            "Team Collaboration",
            "Multi-tenancy",
            "Audit Logging",
            "Data Encryption",
            "Template Gallery",
            "Developer Portal",
          ].map((feature) => (
            <div key={feature} className="flex items-center gap-2 text-sm">
              <div className="h-2 w-2 rounded-full bg-primary" />
              {feature}
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <CTASection
        title="Ready to get started?"
        description="Start your 14-day free trial. No credit card required."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/contact" }}
        background="primary"
      />
    </div>
  );
}
